import React, { useState } from 'react';
import { Eye, EyeOff, ShieldCheck, PenLine, Smartphone, Banknote, Headset } from 'lucide-react';
import { Input } from '../components/ui/input';
import { WorkforceBrand } from './EmployeeShell';

export const WORKFORCE_BG =
  'https://static.prod-images.emergentagent.com/jobs/e28240eb-89e7-4e54-9741-7cd92399b327/images/2c72b3540c478026859db585ad1d3143a7ef4e4590501cdf591e2e9c6060491b.png';

export const PasswordInput = ({ value, onChange, testId, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder}
        className="bg-black/60 border-gray-700 text-white pr-10" data-testid={testId} />
      <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#D4AF37] transition-colors"
        data-testid={`${testId}-toggle`} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

const FEATURES = [
  { icon: ShieldCheck, title: 'Secure onboarding', text: 'Your data is protected every step of the way.' },
  { icon: PenLine, title: 'Electronic signature ready', text: 'Sign documents quickly and securely online.' },
  { icon: Smartphone, title: 'Mobile friendly', text: 'Access your dashboard anytime, anywhere.' },
  { icon: Banknote, title: 'Weekly pay records', text: 'Track your hours and earnings in real time.' },
];

export const WorkforceFeatureStrip = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5 max-w-5xl mx-auto w-full" data-testid="workforce-feature-strip">
    {FEATURES.map((f, i) => (
      <div key={f.title} className={`flex items-start gap-3 lg:px-5 ${i > 0 ? 'lg:border-l lg:border-gray-800' : ''}`}>
        <f.icon size={26} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <div className="text-white font-brand font-bold text-xs sm:text-sm">{f.title}</div>
          <div className="text-gray-400 text-[11px] sm:text-xs mt-0.5 leading-snug">{f.text}</div>
        </div>
      </div>
    ))}
  </div>
);

export const WorkforceHelpLine = () => (
  <div className="flex items-center justify-center gap-2 text-sm" data-testid="workforce-help-line">
    <Headset size={16} className="text-[#D4AF37]" />
    <span className="text-gray-300">Need help?</span>
    <span className="text-[#D4AF37] font-semibold">Contact BuildBroz management.</span>
  </div>
);

export const WorkforceAuthPage = ({ children, showStrip = true }) => (
  <div className="min-h-screen bg-[#050505] relative flex flex-col">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${WORKFORCE_BG})` }} />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/95" />
    <div className="relative z-10 flex flex-col flex-1">
      <header className="flex items-center justify-between px-5 sm:px-10 pt-5">
        <WorkforceBrand />
        <div className="hidden sm:flex items-center gap-2 text-gray-300 text-sm">
          <span className="h-9 w-9 rounded-full border border-[#D4AF37]/50 bg-black/50 flex items-center justify-center">
            <ShieldCheck size={16} className="text-[#D4AF37]" />
          </span>
          Secure. Trusted. Built for You.
        </div>
      </header>
      <main className="flex-1 flex flex-col items-center px-4 py-8 sm:py-10">{children}</main>
      <footer className="px-5 pb-8 space-y-7">
        {showStrip && <WorkforceFeatureStrip />}
        <WorkforceHelpLine />
      </footer>
    </div>
  </div>
);

export const WorkforceHero = ({ subtitle }) => (
  <div className="text-center mb-8">
    <h1 className="font-brand font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
      BuildBroz <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#F5D77A] via-[#D4AF37] to-[#9C7A1E]">Workforce Portal</span>
    </h1>
    <div className="h-px w-64 sm:w-96 mx-auto mt-4 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
    {subtitle && <p className="text-gray-300 text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed">{subtitle}</p>}
  </div>
);
